import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  Plus, RefreshCw, Zap, BookOpen, Search, X, RotateCcw, AlertTriangle,
  BookMarked, ChevronDown, ChevronUp, CheckCircle2, Trash2, ToggleLeft, ToggleRight,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';

const ENTITIES = ['All', 'Eyvo USA', 'Eyvo India'];
const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };
const STATUSES = ['all', 'pending', 'ai_suggested', 'categorized', 'booked'];
const STATUS_COLORS = {
  pending:      'bg-amber-50 text-amber-700',
  ai_suggested: 'bg-blue-50 text-blue-700',
  categorized:  'bg-emerald-50 text-emerald-700',
  booked:       'bg-slate-100 text-slate-500',
};
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(Math.abs(n || 0));
const inputCls = 'border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';

export default function Transactions() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const entitySlug  = pathname.split('/')[2];
  const lockedEntity = ENTITY_MAP[entitySlug] || null;

  const [accountFilter, setAccountFilter] = useState(searchParams.get('account') || '');

  const [txns, setTxns]         = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [entity, setEntity]     = useState(lockedEntity || 'All');
  const [status, setStatus]     = useState('all');
  const [search, setSearch]     = useState(searchParams.get('q') || searchParams.get('search') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [selected, setSelected] = useState(new Set());
  const [aiLoading, setAiLoading]         = useState(false);
  const [showAddManual, setShowAddManual]  = useState(false);
  const [manualForm, setManualForm]        = useState({
    date: '', description: '', amount: '', entity: 'Eyvo USA', bank_account_id: '', currency: 'USD',
  });

  // Rules panel
  const [showRules, setShowRules]     = useState(false);
  const [rules, setRules]             = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [buildingRules, setBuildingRules] = useState(false);
  const [applyingRules, setApplyingRules] = useState(false);

  // Reversal modal
  const [showReverseModal, setReverseModal] = useState(false);
  const [reversalTarget, setReversalTarget] = useState(null);
  const [reversalNote, setReversalNote]     = useState('');
  const [reversalLoading, setRevLoading]    = useState(false);

  const expenseAccounts = accounts.filter(a => ['expense', 'revenue', 'cogs'].includes(a.account_type));
  const bankAccounts    = accounts.filter(a => ['bank', 'credit_card'].includes(a.account_subtype));

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (entity && entity !== 'All') params.set('entity', entity);
    if (status && status !== 'all') params.set('status', status);
    if (search) params.set('search', search);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (accountFilter) params.set('account', accountFilter);
    try {
      const [txnRes, accRes] = await Promise.all([
        api.get(`/api/accounts/transactions?${params}`),
        api.get('/api/accounts/chart'),
      ]);
      setTxns(txnRes.data);
      setAccounts(accRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity, status, search, dateFrom, dateTo, accountFilter]);

  useEffect(() => { load(); }, [load]);

  const categorize = async (txnId, accountId) => {
    await api.put(`/api/accounts/transactions/${txnId}/categorize`, { category_account_id: accountId });
    load();
  };

  const bookSingle = async (txnId) => {
    try {
      await api.post(`/api/accounts/transactions/${txnId}/book`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
  };

  const aiSuggest = async () => {
    const ids = [...selected].filter(id => {
      const txn = txns.find(t => t.transaction_id === id);
      return txn && txn.categorization_status === 'pending';
    });
    if (!ids.length) { toast.error('Select pending transactions first'); return; }
    setAiLoading(true);
    try {
      const res = await api.post('/api/accounts/transactions/ai-suggest', { transaction_ids: ids });
      toast.success(`AI categorized ${res.data.results.filter(r => r.suggested_account_id).length} transactions`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'AI suggestion failed'); }
    setAiLoading(false);
  };

  const saveManual = async () => {
    if (!manualForm.date || !manualForm.description || !manualForm.amount || !manualForm.bank_account_id) {
      toast.error('Fill in all required fields'); return;
    }
    await api.post('/api/accounts/transactions', { ...manualForm, amount: Number(manualForm.amount) });
    setShowAddManual(false);
    setManualForm({ date: '', description: '', amount: '', entity: 'Eyvo USA', bank_account_id: '', currency: 'USD' });
    load();
  };

  const toggleSelect = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const res = await api.get('/api/accounts/categorization-rules');
      setRules(res.data || []);
    } catch (e) { toast.error('Failed to load rules'); }
    setRulesLoading(false);
  }, []);

  const toggleRulesPanel = () => {
    if (!showRules && rules.length === 0) loadRules();
    setShowRules(p => !p);
  };

  const autoBuildRules = async () => {
    setBuildingRules(true);
    try {
      const res = await api.post('/api/accounts/categorization-rules/auto-build');
      toast.success(`Rules built: ${res.data.contact_rules_created} new contact rules, ${res.data.keyword_rules_created} new keyword rules. Total: ${res.data.total_rules}`);
      loadRules();
    } catch (e) { toast.error('Auto-build failed'); }
    setBuildingRules(false);
  };

  const applyRules = async () => {
    setApplyingRules(true);
    try {
      const res = await api.post('/api/accounts/categorization-rules/apply');
      toast.success(`Applied to ${res.data.applied_count} transaction(s)`);
      load();
    } catch (e) { toast.error('Apply failed'); }
    setApplyingRules(false);
  };

  const toggleRule = async (ruleId, current) => {
    await api.patch(`/api/accounts/categorization-rules/${ruleId}`, { is_active: !current });
    setRules(r => r.map(x => x.rule_id === ruleId ? { ...x, is_active: !current } : x));
  };

  const deleteRule = async (ruleId) => {
    await api.delete(`/api/accounts/categorization-rules/${ruleId}`);
    setRules(r => r.filter(x => x.rule_id !== ruleId));
    toast.success('Rule deleted');
  };

  const openReverseModal = (txn) => {
    setReversalTarget(txn);
    setReversalNote('');
    setReverseModal(true);
  };

  const confirmReversal = async () => {
    if (!reversalTarget) return;
    setRevLoading(true);
    try {
      await api.post(`/api/finops/bank-transactions/${reversalTarget.transaction_id}/reverse`, {
        reversal_note: reversalNote || undefined,
      });
      toast.success('Transaction reversed — contra-entry created');
      setReverseModal(false);
      setReversalTarget(null);
      setReversalNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    } finally {
      setRevLoading(false);
    }
  };

  const pending = txns.filter(t => t.categorization_status === 'pending').length;

  return (
    <div className="p-6 fade-in" data-testid="transactions-page">

      {/* Reversal Modal */}
      {showReverseModal && reversalTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="txn-reversal-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RotateCcw size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Reverse Bank Transaction</h3>
                <p className="text-xs text-slate-500">Creates an offsetting contra-entry — audit-logged</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Date</span>
                <span className="font-mono text-slate-700">{reversalTarget.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Description</span>
                <span className="text-slate-700 text-xs truncate max-w-48">{reversalTarget.merchant_name || reversalTarget.description}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className={`font-mono font-bold ${reversalTarget.amount < 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {reversalTarget.amount < 0 ? '+' : '-'}{fmt(reversalTarget.amount)} {reversalTarget.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Bank Account</span>
                <span className="text-slate-700 text-xs">{reversalTarget.bank_account_name}</span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                A synthetic contra-entry dated <strong>today</strong> will be created. The original transaction is preserved for the audit trail.
                Prior periods are not amended.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-700 mb-1">Reason for reversal (optional)</label>
              <textarea
                rows={2}
                value={reversalNote}
                onChange={e => setReversalNote(e.target.value)}
                placeholder="e.g. Posted to wrong account, duplicate entry..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                data-testid="txn-reversal-note-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={confirmReversal}
                disabled={reversalLoading}
                className="flex-1 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-txn-reversal-button"
              >
                {reversalLoading ? (
                  <><RefreshCw size={14} className="animate-spin" /> Reversing…</>
                ) : (
                  <><RotateCcw size={14} /> Confirm Reversal</>
                )}
              </button>
              <button
                onClick={() => { setReverseModal(false); setReversalTarget(null); }}
                className="px-4 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
                data-testid="cancel-txn-reversal-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Transactions</h1>
          {accountFilter
            ? <p className="text-xs text-indigo-700 mt-0.5 font-medium flex items-center gap-1">
                Filtered: {accountFilter}
                <button onClick={() => { setAccountFilter(''); setSearchParams({}); }}
                  className="ml-1 text-slate-400 hover:text-red-500" data-testid="clear-account-filter">
                  <X size={12} />
                </button>
              </p>
            : pending > 0 && <p className="text-xs text-amber-600 mt-0.5">{pending} pending categorization</p>
          }
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <button onClick={aiSuggest} disabled={aiLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100 border border-blue-200 disabled:opacity-50"
              data-testid="ai-suggest-btn">
              <Zap size={13} /> {aiLoading ? 'AI thinking…' : `AI Suggest (${selected.size})`}
            </button>
          )}
          <button
            onClick={toggleRulesPanel}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-md transition-colors ${showRules ? 'bg-indigo-900 text-white border-indigo-900' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid="toggle-rules-btn"
          >
            <BookMarked size={14} /> Rules
            {rules.length > 0 && <span className="text-xs opacity-80">({rules.length})</span>}
            {showRules ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <button onClick={() => setShowAddManual(p => !p)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 rounded-md hover:bg-slate-50"
            data-testid="add-manual-txn-btn">
            <Plus size={14} /> Manual Entry
          </button>
          <Link to="/accounts/bank-connections"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800"
            data-testid="sync-bank-link">
            <RefreshCw size={14} /> Sync Bank
          </Link>
        </div>
      </div>

      {/* ── Categorization Rules Panel ──────────────────────────────────────── */}
      {showRules && (
        <div className="bg-white border border-slate-200 rounded-lg mb-4 overflow-hidden" data-testid="rules-panel">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm text-slate-800">Categorization Rules</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Auto-built from {rules.filter(r => r.match_type === 'contact_name').length} contacts
                + {rules.filter(r => r.match_type === 'keyword').length} keywords
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={applyRules} disabled={applyingRules}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                data-testid="apply-rules-btn">
                <CheckCircle2 size={12} /> {applyingRules ? 'Applying…' : 'Apply to Uncategorized'}
              </button>
              <button onClick={autoBuildRules} disabled={buildingRules}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-900 text-white text-xs font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50"
                data-testid="build-rules-btn">
                <RefreshCw size={12} className={buildingRules ? 'animate-spin' : ''} />
                {buildingRules ? 'Building…' : 'Re-build from History'}
              </button>
            </div>
          </div>

          {rulesLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : rules.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              <BookMarked size={24} className="mx-auto mb-2 text-slate-200" />
              No rules yet. Click "Re-build from History" to generate rules from your transaction history.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs" data-testid="rules-table">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-2 font-semibold text-slate-500">Type</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500">Match Value</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500">Account</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-500">Confidence</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-500">Matches</th>
                    <th className="text-center px-4 py-2 font-semibold text-slate-500">Active</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rules.map(rule => (
                    <tr key={rule.rule_id} className={`hover:bg-slate-50 ${!rule.is_active ? 'opacity-40' : ''}`}
                      data-testid={`rule-row-${rule.rule_id}`}>
                      <td className="px-4 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${rule.match_type === 'contact_name' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {rule.match_type === 'contact_name' ? 'Contact' : 'Keyword'}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-medium text-slate-800 max-w-xs">
                        <span className="truncate block max-w-[200px]" title={rule.match_value}>{rule.match_value}</span>
                      </td>
                      <td className="px-4 py-2 font-mono">
                        <span className="text-slate-700 font-semibold">{rule.account_code}</span>
                        {rule.account_name && (
                          <span className="text-slate-500 ml-1">— {rule.account_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-mono font-semibold ${rule.confidence >= 0.9 ? 'text-emerald-600' : rule.confidence >= 0.7 ? 'text-amber-600' : 'text-red-500'}`}>
                          {(rule.confidence * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{rule.match_count}</td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => toggleRule(rule.rule_id, rule.is_active)}
                          className="text-slate-400 hover:text-indigo-600"
                          data-testid={`toggle-rule-${rule.rule_id}`}>
                          {rule.is_active ? <ToggleRight size={16} className="text-emerald-500" /> : <ToggleLeft size={16} />}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => deleteRule(rule.rule_id)}
                          className="text-slate-300 hover:text-red-500"
                          data-testid={`delete-rule-${rule.rule_id}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Form */}
      {showAddManual && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-3 lg:grid-cols-6 gap-3">
          <input type="date" value={manualForm.date} onChange={e => setManualForm(p => ({ ...p, date: e.target.value }))} className={inputCls} />
          <input placeholder="Description*" value={manualForm.description} onChange={e => setManualForm(p => ({ ...p, description: e.target.value }))} className={`${inputCls} col-span-2`} />
          <input type="number" placeholder="Amount*" value={manualForm.amount} onChange={e => setManualForm(p => ({ ...p, amount: e.target.value }))} className={inputCls} />
          <select value={manualForm.bank_account_id} onChange={e => setManualForm(p => ({ ...p, bank_account_id: e.target.value }))} className={inputCls}>
            <option value="">Bank Account*</option>
            {bankAccounts.map(a => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
          </select>
          <select value={manualForm.entity} onChange={e => setManualForm(p => ({ ...p, entity: e.target.value }))} className={inputCls}>
            <option value="Eyvo USA">Eyvo USA</option>
            <option value="Eyvo India">Eyvo India</option>
          </select>
          <div className="flex gap-2 col-span-3 lg:col-span-6">
            <button onClick={saveManual} className="px-4 py-2 bg-indigo-900 text-white text-xs rounded hover:bg-indigo-800">Save</button>
            <button onClick={() => setShowAddManual(false)} className="text-xs text-slate-500 px-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900" />
        </div>
        <select value={entity} onChange={e => setEntity(e.target.value)} className={inputCls} disabled={!!lockedEntity}>
          {lockedEntity ? <option value={lockedEntity}>{lockedEntity}</option> : ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
          {STATUSES.map(s => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} />
        <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   className={inputCls} />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-3 w-8">
                <input type="checkbox"
                  onChange={e => setSelected(e.target.checked ? new Set(txns.map(t => t.transaction_id)) : new Set())}
                  checked={selected.size === txns.length && txns.length > 0} />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category Account</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>{[...Array(7)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
              ))
            ) : txns.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">No transactions found</td></tr>
            ) : (
              txns.map(txn => {
                const isReversed = txn.is_reversed;
                const isContra   = txn.is_contra;
                const rowCls = isReversed
                  ? 'opacity-50 bg-red-50/20'
                  : isContra
                    ? 'bg-amber-50/30'
                    : '';

                return (
                  <tr key={txn.transaction_id}
                    className={`hover:bg-slate-50 ${selected.has(txn.transaction_id) ? 'bg-blue-50/30' : ''} ${rowCls}`}
                    data-testid={`txn-row-${txn.transaction_id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox"
                        checked={selected.has(txn.transaction_id)}
                        onChange={() => toggleSelect(txn.transaction_id)} />
                    </td>
                    <td className={`px-4 py-3 text-xs font-mono text-slate-500 ${isReversed ? 'line-through' : ''}`}>
                      {txn.date}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className={`text-xs font-medium truncate ${isReversed ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                        {txn.merchant_name || txn.description}
                      </p>
                      {txn.merchant_name && (
                        <p className="text-xs text-slate-400 truncate">{txn.description}</p>
                      )}
                      {isContra && (
                        <span className="mt-0.5 inline-block px-1.5 py-0 rounded text-xs bg-amber-100 text-amber-700 font-medium">CONTRA</span>
                      )}
                    </td>
                    <td className="px-4 py-3 min-w-48">
                      {txn.categorization_status === 'booked' ? (
                        <div>
                          <span className="text-xs font-mono font-semibold text-slate-700">{txn.category_account_id}</span>
                          {txn.category_account_name && (
                            <p className="text-xs text-slate-500 mt-0.5">{txn.category_account_name}</p>
                          )}
                        </div>
                      ) : (
                        <select
                          value={txn.category_account_id || ''}
                          onChange={e => categorize(txn.transaction_id, e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-900"
                          data-testid={`category-select-${txn.transaction_id}`}
                        >
                          <option value="">— Select account —</option>
                          {expenseAccounts.map(a => (
                            <option key={a.account_id} value={a.account_id}>{a.account_code} {a.account_name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono text-xs font-semibold
                      ${txn.amount < 0 ? 'text-emerald-700' : 'text-red-600'}
                      ${isReversed ? 'line-through' : ''}`}>
                      {txn.currency && txn.currency !== 'USD' ? (
                        <div>
                          <div>{txn.amount < 0 ? '+' : '-'}{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(Math.abs(txn.amount))} {txn.currency}</div>
                          <div className="text-slate-400 text-xs">≈ ${(txn.amount_usd || 0).toFixed(2)}</div>
                        </div>
                      ) : (
                        <>{txn.amount < 0 ? '+' : '-'}{fmt(txn.amount)}</>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isReversed ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">REVERSED</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[txn.categorization_status] || 'bg-slate-100 text-slate-600'}`}>
                          {txn.categorization_status}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!isReversed && !isContra && txn.categorization_status === 'categorized' || txn.categorization_status === 'ai_suggested' ? (
                          <button onClick={() => bookSingle(txn.transaction_id)}
                            className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-medium"
                            data-testid={`book-txn-${txn.transaction_id}`}>
                            <BookOpen size={11} /> Book
                          </button>
                        ) : !isReversed && !isContra && txn.categorization_status === 'booked' ? (
                          <span className="text-xs text-slate-400">Posted</span>
                        ) : null}

                        {/* Reverse button — only for Xero transactions that haven't been reversed or are contra-entries */}
                        {txn.source === 'xero' && !isReversed && !isContra && (
                          <button
                            onClick={() => openReverseModal(txn)}
                            className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium"
                            data-testid={`reverse-txn-${txn.transaction_id}`}
                          >
                            <RotateCcw size={11} /> Reverse
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
